import { type CGMReading, normalizeGlucoseReadingForChart, smoothCGM } from '../common'
import { GLUCOSE_THRESHOLDS } from '../types/dashboard'

/**
 * Glycemic-control status derived from TIR stats. Used by
 * ChronicUncontrolledBanner to surface a pet whose CGM data shows a
 * sustained out-of-range pattern without re-using the alert system
 * (per the 2026-06-18 product decision: alerts are for ACTIONABLE
 * episodes; chronic state belongs in dashboard surfaces and weekly
 * summaries, not push notifications).
 */
export type GlycemicControlStatus =
  | 'controlled'
  | 'uncontrolled_high'
  | 'uncontrolled_low'
  | 'mixed'
  | 'insufficient_data'

export interface GlycemicControlResult {
  status: GlycemicControlStatus
  /** TAR % (above target max) — same value as stats.tar. */
  tarPct: number
  /** TBR % (below target min) — same value as stats.tbr. */
  tbrPct: number
  /** Total filtered readings the verdict is based on. */
  totalReadings: number
  /** Human-readable one-line summary suitable for a banner (English; kept for logs/tests). */
  summary: string
  /** i18n key + params for the SAME sentence — what the UI renders per locale. */
  summaryKey: string
  summaryParams: Record<string, number>
}

/**
 * Decision rule (matches the 2026-06-18 chronic-uncontrolled spec):
 *
 *   - uncontrolled_high : TAR > 50%   (>50% above target max)
 *   - uncontrolled_low  : TBR > 5%    (>5% below target min; smaller %
 *                                       because hypo is more dangerous)
 *   - mixed             : both rules trip simultaneously
 *   - controlled        : neither rule trips
 *   - insufficient_data : <12 readings (less than ~1 hour of 3-min cadence)
 *
 * The 12-reading floor mirrors checkSustainedThreshold's
 * MinReadingsForSustained cushion — below this the stats are too
 * noisy for a chronic-state claim. Single-hour windows with sparse
 * data should not get a badge either way.
 */
export function evaluateGlycemicControl(
  stats: TIRComputedStats | null,
  thresholdHighPct: number = 50,
  thresholdLowPct: number = 5,
  minReadings: number = 12,
): GlycemicControlResult {
  if (!stats || stats.total < minReadings) {
    return {
      status: 'insufficient_data',
      tarPct: stats?.tar ?? 0,
      tbrPct: stats?.tbr ?? 0,
      totalReadings: stats?.total ?? 0,
      summary: 'Not enough readings to evaluate chronic state',
      summaryKey: 'pos.glycemic_control.summary.insufficient_data',
      summaryParams: {},
    }
  }
  const high = stats.tar > thresholdHighPct
  const low = stats.tbr > thresholdLowPct

  let status: GlycemicControlStatus = 'controlled'
  let summary = `In target ${stats.tir.toFixed(0)}% of readings`
  let summaryKey = 'pos.glycemic_control.summary.controlled'
  let summaryParams: Record<string, number> = { tir: Number(stats.tir.toFixed(0)) }
  if (high && low) {
    status = 'mixed'
    summary = `Above target ${stats.tar.toFixed(0)}% AND below target ${stats.tbr.toFixed(0)}% — wide swings`
    summaryKey = 'pos.glycemic_control.summary.mixed'
    summaryParams = { tar: Number(stats.tar.toFixed(0)), tbr: Number(stats.tbr.toFixed(0)) }
  } else if (high) {
    status = 'uncontrolled_high'
    summary = `Above target ${stats.tar.toFixed(0)}% of readings (sustained hyperglycemia)`
    summaryKey = 'pos.glycemic_control.summary.uncontrolled_high'
    summaryParams = { tar: Number(stats.tar.toFixed(0)) }
  } else if (low) {
    status = 'uncontrolled_low'
    summary = `Below target ${stats.tbr.toFixed(0)}% of readings (sustained hypoglycemia — clinically urgent)`
    summaryKey = 'pos.glycemic_control.summary.uncontrolled_low'
    summaryParams = { tbr: Number(stats.tbr.toFixed(0)) }
  }
  return {
    status,
    tarPct: stats.tar,
    tbrPct: stats.tbr,
    totalReadings: stats.total,
    summary,
    summaryKey,
    summaryParams,
  }
}

export interface TIRMetricLike {
  glucose: number
  timestamp?: number | Date
}

export interface TIRComputedStats {
  total: number
  veryLowCount: number
  lowCount: number
  inRangeCount: number
  highCount: number
  veryHighCount: number
  avgGlucose: number
  stdDev: number
  cv: number
  tir: number
  tbr: number
  tar: number
  dataCoverage: number
  effectiveDays: number
  /** Highest/lowest values on the SAME normalized+smoothed series as the
   * other stats (matches the trend chart). Timestamps in ms, null when the
   * extreme reading carried no timestamp. */
  peakGlucose: number | null
  peakTimestampMs: number | null
  nadirGlucose: number | null
  nadirTimestampMs: number | null
}

function toMillis(timestamp: number | Date): number {
  if (timestamp instanceof Date) {
    return timestamp.getTime()
  }

  return timestamp > 1000000000000 ? timestamp : timestamp * 1000
}

export function calculateTIRStats(
  metricsData: TIRMetricLike[],
  startDate?: Date,
  endDate?: Date,
  targetMin?: number,
  targetMax?: number,
  highMax?: number,
): TIRComputedStats | null {
  if (!metricsData || metricsData.length === 0) {
    return null
  }

  // Apply the SAME calibration the chart applies so summary stats line
  // up with the visible curve. Pre-fix, the summary computed mean / TIR
  // on raw (uncalibrated) readings while the GlucoseTrendChart rendered
  // BGM-corrected + display-clamped values — producing the reported
  // "AVG GLUCOSE = 19.2 mmol/L over a chart that maxes at ~19" symptom.
  // The DLS BGM power correction (1.46286 * x^0.7971) lowers any
  // reading above ~5.7 mmol/L, so all diabetic data was off until both
  // pipelines used normalizeGlucoseReadingForChart.
  const normalizedMetrics: CGMReading[] = metricsData
    .filter((metric) => metric && Number.isFinite(metric.glucose))
    .map((metric, index) => {
      const rawOffset = (metric as { offset?: number }).offset
      const rawTimestamp = metric.timestamp

      const reading: CGMReading = {
        glucose: metric.glucose,
        timestamp:
          rawTimestamp instanceof Date
            ? rawTimestamp.getTime()
            : typeof rawTimestamp === 'number'
              ? rawTimestamp
              : undefined,
        offset: typeof rawOffset === 'number' ? rawOffset : index,
      }
      return normalizeGlucoseReadingForChart(reading) ?? reading
    })
    .filter((reading): reading is CGMReading => Number.isFinite(reading.glucose))
  if (normalizedMetrics.length === 0) {
    return null
  }

  const filteredMetrics = smoothCGM(normalizedMetrics)
  if (filteredMetrics.length === 0) {
    return null
  }

  const effectiveMin = targetMin ?? GLUCOSE_THRESHOLDS.TARGET_MIN
  const effectiveMax = targetMax ?? GLUCOSE_THRESHOLDS.TARGET_MAX
  const effectiveHigh = highMax ?? GLUCOSE_THRESHOLDS.HIGH

  let veryLowCount = 0
  let lowCount = 0
  let inRangeCount = 0
  let highCount = 0
  let veryHighCount = 0
  let totalGlucose = 0

  filteredMetrics.forEach((metric) => {
    const glucose = metric.glucose
    totalGlucose += glucose

    if (glucose < GLUCOSE_THRESHOLDS.VERY_LOW) {
      veryLowCount++
    } else if (glucose < effectiveMin) {
      lowCount++
    } else if (glucose <= effectiveMax) {
      inRangeCount++
    } else if (glucose <= effectiveHigh) {
      highCount++
    } else {
      veryHighCount++
    }
  })

  const total = filteredMetrics.length
  const avgGlucose = totalGlucose / total
  const stdDev = Math.sqrt(
    filteredMetrics.reduce((sum, metric) => sum + (metric.glucose - avgGlucose) ** 2, 0) / total,
  )
  const cv = avgGlucose > 0 ? (stdDev / avgGlucose) * 100 : 0
  const tir = (inRangeCount / total) * 100
  const tbr = ((veryLowCount + lowCount) / total) * 100
  const tar = ((highCount + veryHighCount) / total) * 100

  const effectiveDays = new Set(
    filteredMetrics
      .filter((metric) => metric.timestamp !== undefined && metric.timestamp !== null)
      .map((metric) =>
        new Date(toMillis(metric.timestamp as number | Date)).toISOString().slice(0, 10),
      ),
  ).size

  // Peak / nadir over the same filtered series, for Key Clinical Findings
  let peakReading = filteredMetrics[0]
  let nadirReading = filteredMetrics[0]
  filteredMetrics.forEach((metric) => {
    if (metric.glucose > peakReading.glucose) peakReading = metric
    if (metric.glucose < nadirReading.glucose) nadirReading = metric
  })
  const readingTimeMs = (reading: CGMReading): number | null =>
    reading.timestamp !== undefined && reading.timestamp !== null
      ? toMillis(reading.timestamp as number | Date)
      : null

  let dataCoverage = 100
  if (startDate && endDate) {
    const durationMs = endDate.getTime() - startDate.getTime()
    const expectedReadings = Math.floor(durationMs / (3 * 60 * 1000))
    dataCoverage = expectedReadings > 0 ? Math.min(100, (total / expectedReadings) * 100) : 100
  }

  return {
    total,
    veryLowCount,
    lowCount,
    inRangeCount,
    highCount,
    veryHighCount,
    avgGlucose,
    stdDev,
    cv,
    tir,
    tbr,
    tar,
    dataCoverage,
    effectiveDays,
    peakGlucose: peakReading.glucose,
    peakTimestampMs: readingTimeMs(peakReading),
    nadirGlucose: nadirReading.glucose,
    nadirTimestampMs: readingTimeMs(nadirReading),
  }
}
